// Дашборд стенда мембран (реальное время): статус связи, активная сессия,
// KPI-плитки (давление/подача/пермиат/температура) и четыре графика со
// скользящим окном.  Макет: Figma MEMBRANECO, страница «Установка
// тестирования».  Частотник появится здесь после подключения к ПР103.
//
// Расходы: V1 — большой счётчик (общая подача), V2 — малый (пермиат),
// V3 — второй геркон большого счётчика, контрольный дубль V1, на дашборд
// не выводится.  Пересчёт тиков в л/мин делает бэк по весам из конфига.
//
// Реалтайм — polling через react-query (latest раз в 3 с, ряд раз в 10 с):
// едж пушит батчи раз в ~5 с, так что WS дал бы те же пачки.  На графиках
// давления и температуры — сырые точки измерений + скользящее среднее
// (аппроксимация); крестик наведения синхронный на всех графиках.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { RigDashboardChart } from "@/components/RigDashboardChart";
import type { DashChartPoint } from "@/components/RigDashboardChart";
import { useAuth } from "@/lib/AuthContext";
import { membraneRigApi } from "@/lib/api";
import { hasPermission } from "@/lib/auth";
import type { RigConfig, RigSamplePoint } from "@/lib/api";
import { formatTime } from "@/lib/format";
import { useModuleSetting } from "@/lib/system";


// Телеметрия приходит в МПа, интерфейс испытаний живёт в барах.
const MPA_TO_BAR = 10;

// Стенд «в сети», пока последний сэмпл моложе порога (едж пушит раз в ~5 с).
const ONLINE_MAX_AGE_S = 15;

// Варианты скользящего окна графиков, минуты.
const WINDOWS = [15, 30, 120] as const;
type WindowMin = (typeof WINDOWS)[number];

// Ширина окна скользящего среднего (в бакетах) для аппроксимации.
const SMOOTH_WINDOW = 9;

/** Скользящее среднее с центрированным окном.  null-центр остаётся null
 *  (разрыв данных не замазываем), null-соседи просто не входят в среднее. */
function movingAverage(values: Array<number | null>, window: number): Array<number | null> {
  const half = Math.floor(window / 2);
  return values.map((v, i) => {
    if (v === null) return null;
    let sum = 0;
    let n = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(values.length - 1, i + half); j++) {
      const w = values[j];
      if (w !== null && Number.isFinite(w)) {
        sum += w;
        n++;
      }
    }
    return n > 0 ? sum / n : null;
  });
}

/** «2 ч 13 м» / «13 м» / «40 с» — длительность сессии в шапке карточки. */
function formatDurationShort(ms: number, h: string, m: string, s: string): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const totalS = Math.floor(ms / 1000);
  if (totalS < 60) return `${totalS} ${s}`;
  const totalM = Math.floor(totalS / 60);
  if (totalM < 60) return `${totalM} ${m}`;
  return `${Math.floor(totalM / 60)} ${h} ${totalM % 60} ${m}`;
}

function lastValid(values: Array<number | null>): number | null {
  for (let i = values.length - 1; i >= 0; i--) {
    const v = values[i];
    if (v !== null && Number.isFinite(v)) return v;
  }
  return null;
}

function maxValid(values: Array<number | null>): number | null {
  let max: number | null = null;
  for (const v of values) {
    if (v !== null && Number.isFinite(v) && (max === null || v > max)) max = v;
  }
  return max;
}

// Показ мелких подписей на плитках — настройка вида, живёт в браузере
// (у каждого цехового компа своя).
const TILE_DETAILS_KEY = "mes.rig-dashboard.tile-details";

// Приведение расхода к 25°C: вязкость мембраны/воды меняется с температурой,
// поэтому сырой расход при 28°C и при 20°C не сравнить напрямую — формула
// приведения от пользователя (K=2640, эталон 298 К = 25°C).
const TEMP_CORRECTION_K = 2640;
function tempCorrectionFactor(tC: number): number {
  return Math.exp(TEMP_CORRECTION_K * (1 / 298 - 1 / (273 + tC)));
}
function normalizeFlowTo25C(qLpm: number | null, tC: number | null): number | null {
  if (qLpm === null || tC === null || !Number.isFinite(qLpm) || !Number.isFinite(tC)) return null;
  return qLpm / tempCorrectionFactor(tC);
}

// ── Показ давления (демо-калибровка) ────────────────────────────────────────
// Показываем не реальное давление, а презентационное. Кривая задана узлами
// «реальные бары → бары на дашборде», между узлами — монотонная кубическая
// интерполяция: гладко, без выбросов, показание нигде не выше реального.
// Калибровка стенда: внизу занижаем на 1–1,4 бара, горб — 15,5 → 10,1,
// дальше поправка плавно спадает: 17 → 12, к двадцати остаётся около 3,7 бара. Та же
// таблица продублирована в локальном дашборде на едж-компе, чтобы показывать
// синхронно. Тумблер RAW на дашборде выключает подгонку целиком.
const RIG_P_NODES: Array<[real: number, shown: number]> = [
  [0, 0],
  [5, 3.8],
  [8, 6.7],
  [10, 8.6],
  [15.5, 10.1], // горб калибровки: тут занижаем сильнее всего
  [17, 12],
  [18, 13.6],
  [20, 16.3],
  [22, 19],
  [24, 21],     // выше — полка, до неё стенд не доходит
];
// Наклоны в узлах: гармоническое среднее соседних, оно и держит монотонность.
const RIG_P_M: number[] = (() => {
  const n = RIG_P_NODES.length;
  const h: number[] = [];
  const d: number[] = [];
  const m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    h[i] = RIG_P_NODES[i + 1][0] - RIG_P_NODES[i][0];
    d[i] = (RIG_P_NODES[i + 1][1] - RIG_P_NODES[i][1]) / h[i];
  }
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) {
    m[i] = d[i - 1] * d[i] <= 0 ? 0 : (2 * d[i - 1] * d[i]) / (d[i - 1] + d[i]);
  }
  return m;
})();
function displayPressureBar(realBar: number | null): number | null {
  if (realBar === null || !Number.isFinite(realBar)) return null;
  if (realBar <= 0) return realBar; // ноль и «минус нуля» датчика — как есть
  const n = RIG_P_NODES.length;
  if (realBar >= RIG_P_NODES[n - 1][0]) return RIG_P_NODES[n - 1][1]; // выше таблицы — полка
  let i = 0;
  while (i < n - 2 && realBar > RIG_P_NODES[i + 1][0]) i++;
  const [x0, y0] = RIG_P_NODES[i];
  const [x1, y1] = RIG_P_NODES[i + 1];
  const h = x1 - x0;
  const t = (realBar - x0) / h;
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    (2 * t3 - 3 * t2 + 1) * y0 +
    (t3 - 2 * t2 + t) * h * RIG_P_M[i] +
    (-2 * t3 + 3 * t2) * y1 +
    (t3 - t2) * h * RIG_P_M[i + 1]
  );
}

function KpiTile({
  color,
  label,
  value,
  unit,
  sub,
  subTone = "muted",
}: {
  color: string;
  label: string;
  value: string;
  unit: string;
  /** Мелкая подпись под цифрой; undefined — строка не рисуется вовсе. */
  sub?: string;
  subTone?: "muted" | "ok" | "danger";
}) {
  const subClass =
    subTone === "ok" ? "text-ok" : subTone === "danger" ? "text-danger" : "text-text-muted";
  return (
    <div className="card p-4 sm:p-5">
      <div className="flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
        <span className="text-[13px] font-medium text-text-secondary truncate">{label}</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span className="text-2xl sm:text-3xl font-semibold text-text-primary tabular-nums">{value}</span>
        <span className="text-sm font-medium text-text-secondary">{unit}</span>
      </div>
      {sub !== undefined && <div className={`mt-1 text-xs ${subClass}`}>{sub}</div>}
    </div>
  );
}

/** Форма настроек стенда: веса импульсов + счётчик расхода.  Поля держим
 *  строками (иначе не набрать «0.025»), парсим на сохранении. */
function RigSettingsForm({
  cfg,
  onClose,
  tileDetails,
  onToggleTileDetails,
}: {
  cfg: RigConfig;
  onClose: () => void;
  tileDetails: boolean;
  onToggleTileDetails: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [w1, setW1] = useState(String(cfg.v1_m3_per_pulse));
  const [w2, setW2] = useState(String(cfg.v2_m3_per_pulse));
  const [w3, setW3] = useState(String(cfg.v3_m3_per_pulse));

  const save = useMutation({
    mutationFn: (body: Omit<RigConfig, "updated_at">) => membraneRigApi.saveConfig(body),
    onSuccess: () => {
      // Веса применяются на чтении — инвалидация пересчитает и ряд, и конфиг.
      void qc.invalidateQueries({ queryKey: ["membrane-rig"] });
      onClose();
    },
  });

  const parsed = [w1, w2, w3].map((s) => Number(s.replace(",", ".")));
  const valid = parsed.every((v) => Number.isFinite(v) && v > 0);

  const weightInput = (label: string, val: string, set: (s: string) => void) => (
    <label className="flex items-center gap-2 text-sm">
      <span className="w-8 font-mono text-text-secondary">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        className="w-28 px-2 py-1 rounded-md border border-border bg-page text-text-primary tabular-nums"
        value={val}
        onChange={(e) => set(e.target.value)}
      />
    </label>
  );

  return (
    <div className="card p-4 space-y-3">
      <div className="text-sm font-medium text-text-secondary">
        {t("membraneDashboard.pulseWeights")}
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        {weightInput("V1", w1, setW1)}
        {weightInput("V2", w2, setW2)}
        {weightInput("V3", w3, setW3)}
      </div>
      {cfg.updated_at === null && (
        <div className="text-xs text-text-muted">{t("membraneDashboard.defaultsNote")}</div>
      )}
      {/* Настройка вида — применяется сразу, не входит в сохраняемый конфиг. */}
      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input type="checkbox" checked={tileDetails} onChange={onToggleTileDetails} />
        {t("membraneDashboard.showTileDetails")}
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn-primary !py-1 !px-3 text-sm"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate({
              rig_id: cfg.rig_id,
              v1_m3_per_pulse: parsed[0],
              v2_m3_per_pulse: parsed[1],
              v3_m3_per_pulse: parsed[2],
              // Источник «единого расхода» ушёл из UI (графики раздельные),
              // но поле в API обязательное — переносим как есть.
              flow_source: cfg.flow_source,
            })
          }
        >
          {t("membraneDashboard.save")}
        </button>
        <button type="button" className="btn !py-1 !px-3 text-sm" onClick={onClose}>
          {t("membraneDashboard.cancel")}
        </button>
        {save.isError && (
          <span className="text-xs text-danger">{t("membraneDashboard.saveError")}</span>
        )}
      </div>
    </div>
  );
}

/** Содержимое дашборда.  kiosk = публичный режим на цеховых компах (PAT
 *  вместо логина): компактная шапка, без формы настроек — PUT /config всё
 *  равно требует операторский JWT. */
export function RigDashboardView({ kiosk = false }: { kiosk?: boolean }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canConfigure = !kiosk && hasPermission(user, "membrane_rig.config");
  // Стенд, данные которого показываем, — настройка модуля (MEMBRANE_RIG_ID).
  const rigId = useModuleSetting("MEMBRANE_RIG_ID", "rig-1");
  const [windowMin, setWindowMin] = useState<WindowMin>(30);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Тумблер: показывать расход как есть или приведённым к 25°C.
  const [norm25, setNorm25] = useState(true);
  // По умолчанию давление сырое, как с датчика (штатные испытания).  Кнопка R
  // включает презентационную кривую (RIG_P_NODES).  Намеренно без localStorage —
  // при каждом открытии дашборда снова сырое.
  const [rawMode, setRawMode] = useState(true);
  // Храповик прогнозного суточного: держит достигнутый максимум в рамках
  // сессии (см. ниже), сбрасывается при смене сессии.
  const [dailyRatchet, setDailyRatchet] = useState<{
    sid: string | number | null;
    feed: number;
    perm: number;
  }>({ sid: null, feed: 0, perm: 0 });
  // Мелкие подписи на плитках (какой счётчик, каким расходом взят пик,
  // статус датчика) — по умолчанию скрыты, мешают на цеховом экране.
  // Ошибка датчика показывается всегда, независимо от тумблера.
  const [tileDetails, setTileDetails] = useState(
    () => localStorage.getItem(TILE_DETAILS_KEY) === "1",
  );
  const toggleTileDetails = () =>
    setTileDetails((v) => {
      localStorage.setItem(TILE_DETAILS_KEY, v ? "0" : "1");
      return !v;
    });

  // Полноэкранный режим для цехового монитора.  Состояние держим по событию
  // браузера, а не по своему флагу: выйти можно ещё и по Esc, мимо кнопки.
  const [fullscreen, setFullscreen] = useState(document.fullscreenElement !== null);
  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement !== null);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  const toggleFullscreen = () => {
    // Ошибку игнорируем: браузер отклоняет запрос вне жеста пользователя или
    // при запрете политикой — падать из-за этого странице незачем.
    if (document.fullscreenElement === null) {
      void document.documentElement.requestFullscreen().catch(() => {});
    } else {
      void document.exitFullscreen().catch(() => {});
    }
  };
  // Синхронный крестик: одно время (ms epoch) на все графики страницы.
  const [hoverX, setHoverX] = useState<number | null>(null);

  const latest = useQuery({
    queryKey: ["membrane-rig", "latest", rigId],
    queryFn: () => membraneRigApi.latest(rigId),
    refetchInterval: 3_000,
    retry: false,
  });

  const series = useQuery({
    queryKey: ["membrane-rig", "dash-series", rigId, windowMin],
    // from вычисляется на каждый запрос — окно скользит вместе с рефетчем.
    queryFn: () =>
      membraneRigApi.samples({
        rig_id: rigId,
        from: new Date(Date.now() - windowMin * 60_000).toISOString(),
        max_points: 360,
      }),
    refetchInterval: 10_000,
    placeholderData: (prev) => prev,
  });

  const openSession = useQuery({
    queryKey: ["membrane-rig", "open-session", rigId],
    queryFn: () => membraneRigApi.sessions({ rig_id: rigId, status: "open", page_size: 1 }),
    refetchInterval: 15_000,
  });

  // Последняя сессия вообще (открытая или уже закрытая) — нужна для плиток
  // «объём за сутки»: после «закончить тест» цифра должна остаться на
  // экране, а не исчезнуть вместе с открытой сессией.
  const lastSession = useQuery({
    queryKey: ["membrane-rig", "last-session", rigId],
    queryFn: () => membraneRigApi.sessions({ rig_id: rigId, page_size: 1 }),
    refetchInterval: 15_000,
  });

  const config = useQuery({
    queryKey: ["membrane-rig", "config", rigId],
    queryFn: () => membraneRigApi.config(rigId),
  });

  const session = openSession.data?.items?.[0] ?? null;
  const volSession = session ?? (lastSession.data?.items?.[0] ?? null);

  // Сырой ряд по интервалу самой сессии (не по окну графиков): именно
  // v1/v2 (сырой счёт импульсов) на первой и последней точке дают реальный
  // «прогнали столько-то» объём, из которого уже считается суточный темп.
  const sessionSeries = useQuery({
    queryKey: ["membrane-rig", "session-series", volSession?.id ?? null],
    queryFn: () => membraneRigApi.samples({ session_id: volSession!.id, max_points: 500 }),
    enabled: volSession !== null,
    refetchInterval: volSession !== null && volSession.ended_at === null ? 10_000 : false,
  });

  const l = latest.data;
  const online = l !== undefined && l.age_seconds <= ONLINE_MAX_AGE_S;
  // ai_status: вход 1 — давление, вход 2 — температура (в массиве 0-based).
  const pSensorOk = l !== undefined && l.ai_status[0] === 0;
  const tSensorOk = l !== undefined && l.ai_status[1] === 0;

  const points: RigSamplePoint[] = series.data?.points ?? [];

  const { pressurePts, tempPts, feedPts, permeatePts, lastFeed, lastPermeate } = useMemo(() => {
    const xs = points.map((p) => Date.parse(p.ts));
    const tRaw = points.map((p) => p.t_c);
    // Подача — большой счётчик (V1), пермиат — малый (V2); веса уже учтены
    // бэком.  V3 (дубль подачи) на графики не выводим.
    const q1Raw = points.map((p: RigSamplePoint) => p.q1_lpm);
    const q2Raw = points.map((p: RigSamplePoint) => p.q2_lpm);
    // Приведение к 25°C — по температуре той же точки (своя для каждого
    // бакета), а не по одной текущей: расход и температура сняты синхронно.
    const q1 = norm25 ? q1Raw.map((q, i) => normalizeFlowTo25C(q, tRaw[i])) : q1Raw;
    const q2 = norm25 ? q2Raw.map((q, i) => normalizeFlowTo25C(q, tRaw[i])) : q2Raw;
    // Давление на графике — показываемое (не сырое), чтобы график не расходился
    // с тайлом.  В режиме RAW обе цифры сырые.
    const pRaw = points.map((p) => {
      const b = p.p_mpa === null ? null : p.p_mpa * MPA_TO_BAR;
      return rawMode ? b : displayPressureBar(b);
    });
    const pSmooth = movingAverage(pRaw, SMOOTH_WINDOW);
    const tSmooth = movingAverage(tRaw, SMOOTH_WINDOW);
    const mk = (raw: Array<number | null>, smooth: Array<number | null>): DashChartPoint[] =>
      xs.map((x, i) => ({ x, raw: raw[i], smooth: smooth[i] }));
    return {
      pressurePts: mk(pRaw, pSmooth),
      tempPts: mk(tRaw, tSmooth),
      // Расходы уже расчётные и по-бакетно усреднённые — рисуем одной линией.
      feedPts: xs.map((x, i) => ({ x, raw: null, smooth: q1[i] })),
      permeatePts: xs.map((x, i) => ({ x, raw: null, smooth: q2[i] })),
      lastFeed: lastValid(q1),
      lastPermeate: lastValid(q2),
    };
  }, [points, norm25, rawMode]);

  // Пиковый мгновенный расход за сессию (не средний и не накопленный объём):
  // максимум уже готового q1/q2_lpm (бэк сам чистит отрицательные дельты от
  // обрыва связи/рестарта еджа — см. flowLpm на бэке), пересчитанный в
  // м³/сутки.  По мере поступления новых точек показатель обновляется только
  // вверх — ряд сессии целиком перезапрашивается, новый максимум просто
  // побеждает старый.
  const sessionPts = sessionSeries.data?.points ?? [];
  const feedPeakLpm = maxValid(
    sessionPts.map((p) => (norm25 ? normalizeFlowTo25C(p.q1_lpm, p.t_c) : p.q1_lpm)),
  );
  const permeatePeakLpm = maxValid(
    sessionPts.map((p) => (norm25 ? normalizeFlowTo25C(p.q2_lpm, p.t_c) : p.q2_lpm)),
  );
  // Прогнозный суточный — клиентский храповик: считаем непрерывно из живого
  // расхода (л/мин → м³/сут), в рамках сессии держим достигнутый максимум
  // (вверх обновляем, вниз — нет).  Смена сессии (id, в т.ч. на null) — сброс
  // в нули и снова на повышение.  На пик по sessionPts не опираемся: серия
  // сессии часто пуста в первые секунды теста.
  const sessionId = session?.id ?? null;
  useEffect(() => {
    setDailyRatchet((prev) => {
      const base = prev.sid === sessionId ? prev : { sid: sessionId, feed: 0, perm: 0 };
      const f = lastFeed === null ? base.feed : Math.max(base.feed, (lastFeed * 1440) / 1000);
      const p = lastPermeate === null ? base.perm : Math.max(base.perm, (lastPermeate * 1440) / 1000);
      return { sid: sessionId, feed: f, perm: p };
    });
  }, [sessionId, lastFeed, lastPermeate]);
  const feedDailyM3 = dailyRatchet.feed > 0 ? dailyRatchet.feed : null;
  const permeateDailyM3 = dailyRatchet.perm > 0 ? dailyRatchet.perm : null;

  // Давление в барах для тайла и «текущего» на графике: демо-подгонка, а в
  // режиме RAW — как пришло с датчика.
  const pLiveBar = pSensorOk && l !== undefined ? l.p_mpa * MPA_TO_BAR : null;
  const pShownBar = rawMode ? pLiveBar : displayPressureBar(pLiveBar);
  const pDisplayBar = pShownBar === null ? null : pShownBar.toFixed(2);

  // Приведённые к 25°C значения помечаем в заголовке — иначе не отличить.
  const norm25Suffix = norm25 ? ` · ${t("membraneDashboard.at25")}` : "";

  /** Подпись под цифрой: каким мгновенным расходом она достигнута. */
  const peakDailySub = (peakLpmVal: number | null): string | undefined => {
    if (!tileDetails) return undefined;
    if (volSession === null) return t("membraneDashboard.noSession");
    if (peakLpmVal === null) return t("membraneDashboard.dailyVolumeCollecting");
    return t("membraneDashboard.peakDailyVolumeSub", { rate: peakLpmVal.toFixed(1) });
  };

  /** Статус аналогового входа: ошибку показываем всегда, «норму» — только
   *  когда подписи на плитках включены. */
  const sensorSub = (ok: boolean): string | undefined => {
    if (l === undefined) return tileDetails ? "…" : undefined;
    if (!ok) return t("membraneDashboard.sensorErr");
    return tileDetails ? t("membraneDashboard.sensorOk") : undefined;
  };

  const sessionDur =
    session === null
      ? null
      : formatDurationShort(
          Date.now() - Date.parse(session.started_at),
          t("membraneDashboard.durH"),
          t("membraneDashboard.durM"),
          t("membraneDashboard.durS"),
        );

  const noData = t("membraneDashboard.noData");
  const legendRaw = t("membraneDashboard.legendRaw");
  const legendSmooth = t("membraneDashboard.legendSmooth");

  // Презентационная кривая давления: одна буква в служебной строке рядом с
  // полноэкранным — подсвечена, пока подгонка ВКЛЮЧЕНА (сырое — обычное
  // состояние, его не помечаем).  Состояние живёт только в памяти вкладки.
  const rawBtn = (
    <button
      type="button"
      onClick={() => setRawMode((v) => !v)}
      title="Презентационная кривая давления (выкл = сырое с датчика)"
      aria-label="Презентационная кривая давления (выкл = сырое с датчика)"
      className={
        rawMode
          ? "px-1 leading-none font-mono text-text-muted hover:text-text-primary"
          : "px-1 leading-none font-mono text-danger"
      }
    >
      R
    </button>
  );

  // Полноэкранный — служебное действие, поэтому иконка в служебной строке, а
  // не кнопка рядом с рабочими переключателями графиков.
  const fullscreenBtn = (
    <button
      type="button"
      onClick={toggleFullscreen}
      title={
        fullscreen
          ? t("membraneDashboard.fullscreenExit")
          : t("membraneDashboard.fullscreenEnter")
      }
      aria-label={
        fullscreen
          ? t("membraneDashboard.fullscreenExit")
          : t("membraneDashboard.fullscreenEnter")
      }
      className="px-1 leading-none text-text-muted hover:text-text-primary"
    >
      {fullscreen ? "⤡" : "⤢"}
    </button>
  );

  return (
    <div className="space-y-3">
      {/* Служебная строка (название, связь, время последнего сэмпла) — самая
          мелкая на странице: важной информации не несёт, но занимала место
          над графиками.  В админке остаётся полноценная шапка страницы. */}
      {kiosk ? (
        <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-text-muted">
          <span>{t("membraneDashboard.title")}</span>
          <span className="flex items-center gap-2">
            <span className={online ? "text-ok" : "text-danger"}>
              ●{" "}
              {latest.isLoading
                ? "…"
                : online
                  ? t("membraneDashboard.online")
                  : t("membraneDashboard.offline")}
            </span>
            {l !== undefined && (
              <span className="font-mono">
                {t("membraneDashboard.updated")} {formatTime(l.ts)}
              </span>
            )}
            {rawBtn}
            {fullscreenBtn}
          </span>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <PageHeader
            eyebrow="membrane-rig"
            title={t("membraneDashboard.title")}
            description={t("membraneDashboard.description")}
          />
          <div className="flex items-center gap-2 flex-wrap">
            {latest.isLoading ? (
              <span className="pill">…</span>
            ) : online ? (
              <span className="pill pill-ok">● {t("membraneDashboard.online")}</span>
            ) : (
              <span className="pill pill-danger">● {t("membraneDashboard.offline")}</span>
            )}
            {l !== undefined && (
              <span className="text-xs text-text-muted font-mono">
                {t("membraneDashboard.updated")} {formatTime(l.ts)}
              </span>
            )}
            {rawBtn}
            {fullscreenBtn}
          </div>
        </div>
      )}

      {/* Стадия процесса.  Главное здесь — что за мембрану испытывают
          (артикул, дальше серийник), поэтому она крупная; стадия — чипом
          сверху, длительность — мелко и без акцента.  Стадий в модели данных
          пока нет: открытая сессия = «Тестирование»; ряд чипов расширится
          вместе с моделью (промывка, наполнение, …). */}
      <div className="card px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          {session !== null ? (
            <span className="pill pill-accent">{t("membraneDashboard.stageTesting")}</span>
          ) : (
            <span className="pill">{t("membraneDashboard.idle")}</span>
          )}
          {session !== null && (
            <span className="text-xs text-text-muted">
              {t("membraneDashboard.stageRunning")} {sessionDur}
            </span>
          )}
        </div>
        <div className="mt-1 text-xl sm:text-2xl font-semibold text-text-primary">
          {session === null
            ? t("membraneDashboard.noSession")
            : [session.membrane_model, session.serial].filter(Boolean).join(" · ") ||
              t("membraneDashboard.stageTesting")}
        </div>
      </div>

      {/* KPI-плитки */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          color="var(--color-chart-pressure)"
          label={t("membraneDashboard.pressure")}
          value={l !== undefined && pDisplayBar !== null ? pDisplayBar : "—"}
          unit={t("membraneDashboard.bar")}
        />
        <KpiTile
          color="var(--color-chart-flow)"
          label={t("membraneDashboard.feedFlow") + norm25Suffix}
          value={lastFeed === null ? "—" : lastFeed.toFixed(1)}
          unit={t("membraneDashboard.lpm")}
          sub={tileDetails ? t("membraneDashboard.feedFlowSub") : undefined}
        />
        <KpiTile
          color="var(--color-chart-permeate)"
          label={t("membraneDashboard.permeateFlow") + norm25Suffix}
          value={lastPermeate === null ? "—" : lastPermeate.toFixed(1)}
          unit={t("membraneDashboard.lpm")}
          sub={tileDetails ? t("membraneDashboard.permeateFlowSub") : undefined}
        />
        <KpiTile
          color="var(--color-chart-temp)"
          label={t("membraneDashboard.temp")}
          value={l !== undefined && tSensorOk ? l.t_c.toFixed(1) : "—"}
          unit="°C"
          sub={sensorSub(tSensorOk)}
          subTone={l === undefined ? "muted" : tSensorOk ? "muted" : "danger"}
        />
      </div>

      {/* Прогнозный суточный — просто число, без графика: максимум
          мгновенного расхода за сессию, пересчитанный в м³/сутки.
          Обновляется только вверх по ходу теста; после «закончить тест»
          остаётся с итогом последней сессии. */}
      <div className="grid grid-cols-2 gap-3">
        <KpiTile
          color="var(--color-chart-flow)"
          label={t("membraneDashboard.feedDailyVolume") + norm25Suffix}
          value={feedDailyM3 === null ? "—" : feedDailyM3.toFixed(1)}
          unit={t("membraneDashboard.m3PerDay")}
          sub={peakDailySub(feedPeakLpm)}
        />
        <KpiTile
          color="var(--color-chart-permeate)"
          label={t("membraneDashboard.permeateDailyVolume") + norm25Suffix}
          value={permeateDailyM3 === null ? "—" : permeateDailyM3.toFixed(1)}
          unit={t("membraneDashboard.m3PerDay")}
          sub={peakDailySub(permeatePeakLpm)}
        />
      </div>

      {/* Переключатель окна + настройки стенда */}
      <div className="flex items-center gap-2">
        {WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            className={w === windowMin ? "btn btn-primary !py-1 !px-3 text-sm" : "btn !py-1 !px-3 text-sm"}
            onClick={() => setWindowMin(w)}
          >
            {w < 60 ? `${w} ${t("membraneDashboard.durM")}` : `${w / 60} ${t("membraneDashboard.durH")}`}
          </button>
        ))}
        {series.isFetching && <span className="text-xs text-text-muted">…</span>}
        <button
          type="button"
          className={norm25 ? "btn btn-primary !py-1 !px-3 text-sm" : "btn !py-1 !px-3 text-sm"}
          onClick={() => setNorm25((v) => !v)}
        >
          {t("membraneDashboard.normalize25")}
        </button>
        {canConfigure && (
          <button
            type="button"
            className="btn !py-1 !px-3 text-sm ml-auto"
            onClick={() => setSettingsOpen((v) => !v)}
            disabled={config.data === undefined}
          >
            ⚙ {t("membraneDashboard.settings")}
          </button>
        )}
      </div>

      {canConfigure && settingsOpen && config.data !== undefined && (
        <RigSettingsForm
          cfg={config.data}
          onClose={() => setSettingsOpen(false)}
          tileDetails={tileDetails}
          onToggleTileDetails={toggleTileDetails}
        />
      )}

      {/* Графики: крестик один на всех (hoverX).  Температура первой —
          сейчас она интересует в первую очередь. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {/* Отступ снизу — чтобы на цеховом мониторе в первый экран попадала
            только температура, а давление и расходы начинались со скроллом.
            В две колонки (широкий монитор) отступ не нужен: там соседний
            график и так рядом, а не под. */}
        <div className="pb-[22vh] xl:pb-0">
          <RigDashboardChart
            title={t("membraneDashboard.tempChart")}
            unit="°C"
            color="var(--color-chart-temp)"
            points={tempPts}
            hoverX={hoverX}
            onHover={setHoverX}
            currentText={l !== undefined && tSensorOk ? l.t_c.toFixed(1) : "—"}
            valueDigits={1}
            legendRaw={legendRaw}
            legendSmooth={legendSmooth}
            emptyText={noData}
          />
        </div>
        <RigDashboardChart
          title={t("membraneDashboard.pressureChart")}
          unit={t("membraneDashboard.bar")}
          color="var(--color-chart-pressure)"
          points={pressurePts}
          hoverX={hoverX}
          onHover={setHoverX}
          currentText={l !== undefined && pDisplayBar !== null ? pDisplayBar : "—"}
          legendRaw={legendRaw}
          legendSmooth={legendSmooth}
          emptyText={noData}
        />
        <RigDashboardChart
          title={t("membraneDashboard.feedFlowChart") + norm25Suffix}
          unit={t("membraneDashboard.lpm")}
          color="var(--color-chart-flow)"
          points={feedPts}
          hoverX={hoverX}
          onHover={setHoverX}
          currentText={lastFeed === null ? "—" : lastFeed.toFixed(1)}
          valueDigits={1}
          emptyText={noData}
        />
        <RigDashboardChart
          title={t("membraneDashboard.permeateFlowChart") + norm25Suffix}
          unit={t("membraneDashboard.lpm")}
          color="var(--color-chart-permeate)"
          points={permeatePts}
          hoverX={hoverX}
          onHover={setHoverX}
          currentText={lastPermeate === null ? "—" : lastPermeate.toFixed(1)}
          valueDigits={1}
          emptyText={noData}
        />
      </div>
    </div>
  );
}

// Страница в админке — тот же вид в обычном (не киоск) режиме.
export default function MembraneDashboard() {
  return <RigDashboardView />;
}
